<template>
	<div
		class="swiper-container"
		v-observe-visibility="autoSwipeOnVisiblity ? observeVisibilityOptions : false"
		@click="clearAutoSwipeInterval"
		@touchstart="clearAutoSwipeInterval"
	>
		<div class="item-container" ref="itemContainer" dir="ltr">
			<div class="swiper-wrapper">
				<slot></slot>
			</div>
		</div>

		<div
			class="swiper-bullets swiper-bullets--bottom"
			:class="{ 'swiper-bullets-static': isPPSwiper && arrowStatic }"
		>
			<div v-if="!hideControls">
				<FontAwesomeIcon :icon="faChevronLeft" class="pointer" @click="prev" />
				<span class="slide-count"> {{ currentSlideIndex + 1 }}/{{ props.itemCount }} </span>
				<FontAwesomeIcon :icon="faChevronRight" class="pointer" @click="next" />
			</div>
			<div class="line-container" :class="{ fullLine, hideControls, 'is-promoted': isPromoted }">
				<div class="line" />
			</div>
			<span v-if="isPromoted" class="promoted-text">{{ $t('WEBAPP_PROMOTED') }}</span>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { AppDirection } from '@/enums/app-direction';
import { captureMessageForSentry } from '@/libraries/sentry';
import { useMounted, usePointerSwipe } from '@vueuse/core';
import type { SwiperInstance } from 'tiny-swiper';

import { computed, onActivated, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import { NATIVE_AD_RENDERED_KEY } from '@/features/native-ads/helpers/native-ad-config';
import { storeToRefs } from 'pinia';
import { useMetaStore } from '@/stores/pinia/useMetaStore';

let swiper: SwiperInstance | null = null;
const currentSlideIndex = ref(0);
const itemContainer = ref<HTMLElement | null>(null);
const isMounted = useMounted();

const props = withDefaults(
	defineProps<{
		itemCount: number;
		delay: number;
		initialSlide?: number;
		persistedSlideIndex: number;
		fullLine?: boolean;
		isPPSwiper?: boolean;
		isPromoted?: boolean;
		hideControls?: boolean;
		arrowStatic?: boolean;
		/* persistedAutoSwipe - interval in ms for repeated autoswipe until user interaction */
		persistedAutoSwipe?: number;
		isNativeSQTVActive?: boolean;
	}>(),
	{
		initialSlide: 0,
		showTitle: true,
		hideControls: false,
		arrowStatic: true,
		isNativeSQTVActive: false,
	}
);
const emits = defineEmits(['slideChange']);
const { appDirection } = storeToRefs(useMetaStore());

/**
 * Only auto swipe after swiper in in the viewport
 */
const autoSwipeOnVisiblity = computed(() => props.isPPSwiper && props.initialSlide > 0);

if (!IS_TOUCH) {
	const { isSwiping } = usePointerSwipe(itemContainer);
	// Ignore click events *while* swiping on desktop
	watch(isSwiping, isCurrentlySwiping => {
		if (!isCurrentlySwiping) return;

		itemContainer.value?.addEventListener(
			'click',
			e => {
				e.preventDefault();
				e.stopPropagation();
			},
			{ once: true, capture: true }
		);
	});
}

const isRTL = computed(() => appDirection.value === AppDirection.RTL);

const hasAutoSwiped = ref(false);
const hasBeenObserved = ref(false);
const nativeAdHasRendered = ref(false);
const autoSwipeIntervalId = ref<ReturnType<typeof setInterval> | null>(null);

provide(NATIVE_AD_RENDERED_KEY, onNativeAdRendered);

onActivated(initSwiper);

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

onMounted(() => {
	initSwiper();
	window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize);
	clearAutoSwipeInterval();
});

function clearAutoSwipeInterval() {
	if (autoSwipeIntervalId.value) {
		clearInterval(autoSwipeIntervalId.value);
		autoSwipeIntervalId.value = null;
	}
}

function startAutoSwipe() {
	if (hasAutoSwiped.value) return;
	hasAutoSwiped.value = true;

	// Swipe forwards after delay
	setTimeout(() => {
		swiper?.slideTo(props.initialSlide);
		changeSlideIndex({ toIndex: props.initialSlide, direction: 'right', nonInteraction: true });
	}, props.delay);

	// Continuous auto swipe - currently only required for Native SQTV
	if (props.persistedAutoSwipe) {
		autoSwipeIntervalId.value = setInterval(() => {
			const toIndex = (currentSlideIndex.value + 1) % props.itemCount;
			swiper?.slideTo(toIndex);
			changeSlideIndex({ toIndex, direction: 'right', nonInteraction: true });
		}, props.persistedAutoSwipe);
	}
}

function onVisibilityChanged(isVisible: boolean) {
	if (isVisible && !hasBeenObserved.value) {
		hasBeenObserved.value = true;

		if (!props.isNativeSQTVActive) {
			startAutoSwipe();
		} else if (nativeAdHasRendered.value) {
			startAutoSwipe();
		}
	}
}

function next(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		prev(true);
		return;
	}

	const toIndex = currentSlideIndex.value === props.itemCount - 1 ? 0 : currentSlideIndex.value + 1;
	swiper?.slideTo(toIndex);
	changeSlideIndex({ toIndex, direction: 'right' });
}

function prev(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		next(true);
		return;
	}

	const toIndex = currentSlideIndex.value === 0 ? props.itemCount - 1 : currentSlideIndex.value - 1;
	swiper?.slideTo(toIndex);
	changeSlideIndex({ toIndex, direction: 'left' });
}

function changeSlideIndex({
	toIndex,
	direction,
	nonInteraction = false,
}: {
	toIndex: number;
	direction: 'left' | 'right';
	nonInteraction?: boolean;
}): void {
	const prevIndex = currentSlideIndex.value;
	currentSlideIndex.value = toIndex;

	if (prevIndex !== null && toIndex !== prevIndex && isMounted.value) {
		emits('slideChange', {
			direction: direction,
			position: toIndex,
			nonInteraction: nonInteraction,
		});
	}
}

function initSwiper(persistedSlideIndex: number = 0) {
	try {
		// swiper has a top level global variable calling `requestAnimationFrame` so the import needs to be client only
		// Because of a watcher in the tree, this function should not be async
		import('tiny-swiper').then(({ default: Swiper }) => {
			if (itemContainer.value && import.meta.client) {
				if (swiper == null) {
					swiper = new Swiper(itemContainer.value, {
						// initialSlide: persistedSlideIndex,
						loop: false,
					});
				}

				if (!autoSwipeOnVisiblity.value) {
					if (persistedSlideIndex === 0 && props.initialSlide > 0) {
						setTimeout(() => {
							swiper?.slideTo(props.initialSlide);
							changeSlideIndex({ toIndex: props.initialSlide, direction: 'right', nonInteraction: true });
						}, props.delay);
					} else {
						currentSlideIndex.value = persistedSlideIndex;
					}
				}
				swiper.on('before-slide', (current: number, state: any, next: number) => {
					// on element swipe state.isTouching === true
					if (state?.isTouching) {
						const direction = current > next ? 'left' : 'right';
						changeSlideIndex({
							toIndex: next,
							direction,
							nonInteraction: false,
						});
					}
				});
			}
		});
	} catch (error: any) {
		captureMessageForSentry('[Initialize Tiny Swiper]', { error, where: '[TinySwiper]: initSwiper' }, 'error');
	}
}

function handleResize() {
	if (swiper?.update) {
		swiper.update();
	}
}

/* Native SQTV only
 * Provide callback for NativeAdSQTV to call when ad renders
 * We want to delay autoswipe until we confirm the ad has rendered
 */

function onNativeAdRendered() {
	if (!props.isNativeSQTVActive) return;

	nativeAdHasRendered.value = true;

	// If we've already observed the component, start autoswipe immediately
	if (hasBeenObserved.value && !hasAutoSwiped.value) {
		startAutoSwipe();
	}
}

defineExpose({
	next,
	prev,
	currentSlideIndex,
});
</script>

<style lang="scss" scoped>
.swiper-container {
	width: 100%;
	height: 100%;
	position: relative;
	overflow: hidden;
	z-index: 1;
	display: flex;
	flex-direction: column;

	.swiper-pagination {
		position: absolute;
		top: 50%;
		inset-inline-end: 16px;
		transform: translateY(-50%);
		z-index: 2;
		font-size: 16px;
		background: #fff;
		color: #000000;
		border-radius: 50%;
		width: 32px;
		height: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
		&.left {
			inset-inline-start: 16px;
		}
	}
	.swiper-bullets {
		position: absolute;
		&.top {
			padding: 16px;
		}
		&-static {
			position: static;
		}
		width: 95%;
		top: 0;
		padding: 0 16px 12px 16px;
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		gap: 8px;
		direction: ltr;
		z-index: 2;
		cursor: pointer;
		@media screen and (max-width: 1024px) {
			padding: 0 16px 16px 16px;
		}
		&--bottom {
			justify-content: center;
			bottom: 0px;
			top: unset;
			&.extra-padding {
				margin-bottom: 6px;
			}
			&.v1-spacing {
				@media screen and (min-width: 1024px) {
					bottom: 5px;
				}
			}
		}

		.swiper-bullet {
			flex: 1;
			height: 2px;
			background: #6f7d90;
			display: inline-block;
			&.active {
				background: #ffff;
			}
			&.rounded {
				flex: unset;
				width: 8px;
				height: 8px;
				border-radius: 50%;
			}
		}
	}
	.swiper-title {
		padding: 0 16px;
		min-height: 48px;
	}
}
.swiper-wrapper {
	display: flex;
	flex-direction: row;
	:deep(.swiper-item) {
		position: absolute;
	}
}

.item-container {
	border-radius: 8px;
}
.line-container {
	flex: 1;
	display: flex;
	padding-inline-end: 104px;
	padding-inline-start: 16px;

	&.is-promoted {
		padding-inline-end: 16px;
	}
	&.fullLine {
		padding-inline-end: 0px;
	}
	&.hideControls {
		padding-inline-start: 0px;
	}
}
.line {
	width: 100%;
	height: 1px;
	background-color: #39424d;
}
.promoted-text {
	margin: 0;
	color: #6f7d90;
	font-size: 14px;
	line-height: 16px;
	letter-spacing: 0.21px;
	display: flex;
	align-items: center;
	justify-content: center;
}
.slide-count {
	margin: 0 4px;
}
</style>
